import { useEffect } from "react";
import posthog from "posthog-js";
import { isPostHogEnabled } from "@/config/posthog";
import { useBoundStore } from "@/stores";

declare const __APP_VERSION__: string;

/**
 * Stable, analytics-friendly labels: trim, lowercase, spaces → underscores, collapse repeats.
 */
function normalizeAnalyticsName(raw: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return s || "unknown";
}

/**
 * Registers PostHog super-properties after app config and auth are available.
 * Mount once under App (inside AppConfigProvider + AuthProvider).
 */
export function usePostHogSuperProperties(): void {
  const countryId = useBoundStore.use.countryId();
  const clientId = useBoundStore.use.clientId();
  const defaultPhoneCountry = useBoundStore.use.defaultPhoneCountry();
  const countryName = useBoundStore.use.countryName();
  const clientName = useBoundStore.use.clientName();
  const isAuthenticated = useBoundStore.use.isAuthenticated();

  useEffect(() => {
    if (!isPostHogEnabled()) return;
    if (countryId == null || clientId == null) return;

    const countryRaw =
      countryName?.trim() ||
      (defaultPhoneCountry ? String(defaultPhoneCountry) : null) ||
      `country_${countryId}`;
    const clientRaw = clientName?.trim() || `client_${clientId}`;

    const countryNameValue = normalizeAnalyticsName(countryRaw);
    const clientNameValue = normalizeAnalyticsName(clientRaw);

    const appVersion = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown";

    posthog.register({
      country_name: countryNameValue,
      client_name: clientNameValue,
      country_code: defaultPhoneCountry ?? undefined,
      app_version: appVersion,
      user_type: isAuthenticated ? "registered" : "anonymous",
    });

    posthog.setPersonProperties({
      country_name: countryNameValue,
      client_name: clientNameValue,
      country_code: defaultPhoneCountry ?? undefined,
    });
  }, [countryId, clientId, defaultPhoneCountry, countryName, clientName, isAuthenticated]);
}
